import { gql, useQuery } from "@apollo/client";
import { LatestProviderLocationsQuery, LocationActivityEnum } from "~/graphql/__generated__/graphql";
import { IconButton, Skeleton, Typography } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";
import { coordinateToMapPoint, MapMarkerInput } from "@caredna-marlin/map-wrapper";
import { default as GenericMapWrapper } from "@caredna-marlin/map-wrapper/google-map-wrapper";
import { useMemo, useState } from "react";

import { Permission } from "~/hooks/permissions";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import PermissionOrWarn from "~/components/common/PermissionOrWarn";
import PatientLocationSearchBox, { mapHeight, mapWidth } from "../route/findPatientLocation";
import { useMapPatientInfo } from "~/state/map/mapPatientInfo";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";

interface ProviderLocationData {
  latitude: string | null;
  longitude: string | null;
  timestamp: string | null;
  activity: string | null;
  clockStatus: string | null;
  clockedAt: string | null;
  provider: {
    firstName: string | null;
    lastName: string | null;
  } | null;
}

function shouldShowProvider(providerLocation: ProviderLocationData): boolean {
  if (!providerLocation.timestamp) return false;
  
  const locationTime = new Date(providerLocation.timestamp);
  const now = new Date();
  const ageMinutes = Math.floor((now.getTime() - locationTime.getTime()) / (1000 * 60));
  const isClockIn = providerLocation.clockStatus === 'clock_in';

  if (!isClockIn && ageMinutes > 15) return false;
  
  return true;
}

function getProviderLabel(providerLocation: ProviderLocationData): string {
  const providerName = `${providerLocation.provider?.firstName || ''} ${providerLocation.provider?.lastName || ''}`.trim();
  
  if (!providerLocation.timestamp) return providerName;
  
  const locationTime = new Date(providerLocation.timestamp);
  const now = new Date();
  const ageMinutes = Math.floor((now.getTime() - locationTime.getTime()) / (1000 * 60));
  const isClockIn = providerLocation.clockStatus === 'clock_in';

  if (isClockIn) {
    if (ageMinutes < 30) {
      return providerName;
    } else {
      return `${providerName}<br/>(${ageMinutes} minutes ago)`;
    }
  } else {
    return providerName;
  }
}

function isProviderClockIn(providerLocation: ProviderLocationData): boolean {
  return providerLocation.clockStatus === 'clock_in';
}

export default function LocationIndexView() {
  return <PermissionOrWarn permission={Permission.viewProviderLocations}>
    <ProviderLocations />
  </PermissionOrWarn>
}

function ProviderLocations() {
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const {mapPatientInfo, loadingPatientInfo, setMapPatientId} = useMapPatientInfo();
  const orgConfig = useOrgConfig();
  const { timeFormatPreference } = useZonedDateFormat();

  const { data, loading } = useQuery<LatestProviderLocationsQuery>(PROVIDER_QUERY, {
    pollInterval: 60000, // 1 minute
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true, // Otherwise onCompleted doesn't fire when polling
    onCompleted: () => {
      setLastUpdated(new Date());
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const navigate = useNavigate();

  const markerInputs = useMemo(() => {
    if (!data?.latestProviderLocations) return [];

    const markerInputList: MapMarkerInput[] = []

    data.latestProviderLocations.forEach((providerLocation) => {
      if (providerLocation.provider && shouldShowProvider(providerLocation)) {
        const point = coordinateToMapPoint(`${providerLocation.latitude},${providerLocation.longitude}`)
        if (point) {
          markerInputList.push({
            label: getProviderLabel(providerLocation),
            timestamp: new Date(providerLocation.timestamp || new Date()),
            highlighted: isProviderClockIn(providerLocation),
            icon: providerLocation.activity == LocationActivityEnum.InVehicle ? "car" : "place",
            point: point
          })
        }
      }
    })

    return markerInputList;
  }, [data]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton size="small" onClick={() => { navigate(-1) }} color="primary">
          <ArrowBackIosIcon />
        </IconButton>
        <h1>Provider Locations
          <Typography style={{marginLeft: 5}} variant="caption">{lastUpdated ? `As of ${lastUpdated.toLocaleTimeString('en-US', { 
            hour12: timeFormatPreference === 'standard',
            hour: '2-digit', 
            minute: '2-digit' 
          })}` : ""}</Typography>
        </h1>
      </div>
      <PatientLocationSearchBox mapPatientInfo={mapPatientInfo} setMapPatientId={setMapPatientId} loadingPatientInfo={loadingPatientInfo}/>
      {loading && !data ? (
        <Skeleton variant="rectangular" width={mapWidth} height={mapHeight} />
      ) : (
        <GenericMapWrapper
          mapsApiKey={orgConfig.GOOGLE_MAPS_WEB_API_KEY}
          displayRoute={false}
          clusterMarkers={true}
          height={mapHeight}
          width={mapWidth}
          markerInputs={markerInputs}
          trackingPoints={[]}
          targetLocation={mapPatientInfo?.location}
          onPatientClick={() => {}} />
      )}
    </>
  );
}

const PROVIDER_QUERY = gql`
  query LatestProviderLocations
  @permission(requires: viewProviderLocations)
  {
    latestProviderLocations {
      latitude
      longitude
      timestamp
      activity
      clockStatus
      clockedAt
      provider {
        firstName
        lastName
      }
    }
  }
`
